// AcademicProjectClassTeacherManager.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Link,
  Pagination,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GradingIcon from "@mui/icons-material/Grading";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import GitHubIcon from "@mui/icons-material/GitHub";
import YouTubeIcon from "@mui/icons-material/YouTube";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import NotesIcon from "@mui/icons-material/Notes";
import { formatDateTimeIST } from "./commonfunctions/dateUtils";
import { API_URL } from "./config";

const AcademicProjectClassTeacherManager = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "#1565C0";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    borderRadius: 2,
    overflow: "hidden",
  };
  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      whiteSpace: "nowrap",
    },
  };
  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
  });
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
  };

  const token = localStorage.getItem("auth") || localStorage.getItem("token");
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tab, setTab] = useState(0);
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [assignmentGroupOptions, setAssignmentGroupOptions] = useState([]);
  const [scope, setScope] = useState({
    AcademicYear: "",
    SubCampusCode: "",
    DegreeCode: "",
    BranchCode: "",
    Regulation: "",
    SubjectNameCode: "",
    Semester: "",
    Section: "",
    AssignmentGroupCode: "",
  });

  const normalizeExternalUrl = (url) => {
    if (!url) return "";
    const trimmed = String(url).trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };
  
  const formatChecklistLabel = (key) =>
    String(key)
      .replace(/([A-Z])/g, " $1")
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (c) => c.toUpperCase());
  {/*
  const classScopeOptions = teacherClasses.map((x) => ({
    label: `${x.Regulation || "-"} / Sub ${x.SubjectNameCode || "-"} / Sec ${x.Section || "-"}`,
    Regulation: x.Regulation || "",
    SubjectNameCode: x.SubjectNameCode || "",
    Section: x.Section || "",
  }));
  */}
  const [projects, setProjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [expandedAssignmentID, setExpandedAssignmentID] = useState(null);
  const [expandedProjectID, setExpandedProjectID] = useState(null);

  const [assignProjectFilter, setAssignProjectFilter] = useState("");
  const [assignStudentSearch, setAssignStudentSearch] = useState("");
  const [assignPage, setAssignPage] = useState(1);

  const [gradeProjectFilter, setGradeProjectFilter] = useState("");
  const [gradeStudentSearch, setGradeStudentSearch] = useState("");
  const [gradeStatusFilter, setGradeStatusFilter] = useState("");
  const [gradePage, setGradePage] = useState(1);

  const ROWS_PER_PAGE = 10;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchTeacherClasses = async () => {
    const res = await axios.get(`${API_URL}/api/academic-projects/class-teacher/classes`, {
      headers: authHeaders,
    });
    setTeacherClasses(res.data || []);
  };

  const fetchAcademicYears = async () => {
    const res = await axios.get(`${API_URL}/api/ai-lessons/builder/academicyears`, {
      headers: authHeaders,
    });

    const rows = Array.isArray(res.data) ? res.data : [];
    setAcademicYearOptions(
      rows
        .map((x) => x.AcademicYearLabel || x.AcademicYear || "")
        .filter(Boolean)
    );
  };

  const fetchAssignmentGroups = async () => {
    if (
      !scope.AcademicYear ||
      !scope.SubCampusCode ||
      !scope.DegreeCode ||
      !scope.BranchCode ||
      !scope.Regulation ||
      !scope.Semester ||
      !scope.Section
    ) {
      setAssignmentGroupOptions([]);
      return;
    }

    const res = await axios.get(
      `${API_URL}/api/academic-projects/class-teacher/assignment-groups`,
      {
        headers: authHeaders,
        params: {
          AcademicYear: scope.AcademicYear,
          SubCampusCode: scope.SubCampusCode,
          DegreeCode: scope.DegreeCode,
          BranchCode: scope.BranchCode,
          Regulation: scope.Regulation,
          Semester: scope.Semester,
          Section: scope.Section,
        },
      }
    );

    setAssignmentGroupOptions(res.data || []);
  };

  const fetchProjects = async () => {
    if (!scope.AcademicYear || !scope.Section || !scope.AssignmentGroupCode) {
      setProjects([]);
      return;
    }

    const res = await axios.get(`${API_URL}/api/academic-projects/class-teacher/projects`, {
      headers: authHeaders,
      params: scope,
    });

    setProjects(res.data || []);
  };

  const fetchStudents = async () => {
    if (!scope.AcademicYear || !scope.Section || !scope.AssignmentGroupCode) {
      setStudents([]);
      return;
    }

    const res = await axios.get(`${API_URL}/api/academic-projects/class-teacher/students`, {
      headers: authHeaders,
      params: scope,
    });

    setStudents(res.data || []);
  };

  const fetchAssignments = async () => {
    if (!scope.AcademicYear || !scope.Section || !scope.AssignmentGroupCode) {
      setAssignments([]);
      return;
    }

    const res = await axios.get(`${API_URL}/api/academic-projects/class-teacher/assignments`, {
      headers: authHeaders,
      params: scope,
    });

    setAssignments(res.data || []);
  };

  const fetchAnalytics = async () => {
    if (!scope.AcademicYear || !scope.Section || !scope.AssignmentGroupCode) {
      setAnalytics({});
      return;
    }

    const res = await axios.get(`${API_URL}/api/academic-projects/class-teacher/analytics`, {
      headers: authHeaders,
      params: scope,
    });

    setAnalytics(res.data || {});
  };

  const refreshAll = async () => {
    setLoading(true);
    setError("");

    try {
      await Promise.all([
        fetchAssignmentGroups(),
        fetchProjects(),
        fetchStudents(),
        fetchAssignments(),
        fetchAnalytics(),
      ]);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to refresh data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeacherClasses().catch(() => setError("Failed to load teacher classes"));
    fetchAcademicYears().catch(() => setError("Failed to load academic years"));
  }, []);

  useEffect(() => {
    refreshAll();
  }, [scope]);

  const uniqueValues = (rows, field) =>
    [...new Set(rows.map((x) => x[field]).filter(Boolean))];

  const classesForDegree = teacherClasses.filter(
    (x) => !scope.SubCampusCode || x.SubCampusCode === scope.SubCampusCode
  );

  const classesForBranch = classesForDegree.filter(
    (x) => !scope.DegreeCode || x.DegreeCode === scope.DegreeCode
  );

  const classesForRegulation = classesForBranch.filter(
    (x) => !scope.BranchCode || x.BranchCode === scope.BranchCode
  );

  const classesForSemester = classesForRegulation.filter(
    (x) => !scope.Regulation || x.Regulation === scope.Regulation
  );

  const classesForSection = classesForSemester.filter(
    (x) => !scope.Semester || String(x.Semester) === String(scope.Semester)
  );

  const scopeOptions = {
    AcademicYear: academicYearOptions,
    SubCampusCode: uniqueValues(teacherClasses, "SubCampusCode"),
    DegreeCode: uniqueValues(classesForDegree, "DegreeCode"),
    BranchCode: uniqueValues(classesForBranch, "BranchCode"),
    Regulation: uniqueValues(classesForRegulation, "Regulation"),
    Semester: uniqueValues(classesForSemester, "Semester"),
    Section: uniqueValues(classesForSection, "Section"),
    AssignmentGroupCode: assignmentGroupOptions
      .map((x) => x.AssignmentGroupCode)
      .filter(Boolean),
  };

  const handleScopeChange = (e) => {
    const { name, value } = e.target;

    setScope((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "AcademicYear") {
        return { ...next, AssignmentGroupCode: "" };
      }

      if (name === "SubCampusCode") {
        return {
          ...next,
          DegreeCode: "",
          BranchCode: "",
          Regulation: "",
          Semester: "",
          Section: "",
          SubjectNameCode: "",
          AssignmentGroupCode: "",
        };
      }

      if (name === "DegreeCode") {
        return {
          ...next,
          BranchCode: "",
          Regulation: "",
          Semester: "",
          Section: "",
          SubjectNameCode: "",
          AssignmentGroupCode: "",
        };
      }

      if (name === "BranchCode") {
        return {
          ...next,
          Regulation: "",
          Semester: "",
          Section: "",
          SubjectNameCode: "",
          AssignmentGroupCode: "",
        };
      }

      if (name === "Regulation") {
        return {
          ...next,
          Semester: "",
          Section: "",
          SubjectNameCode: "",
          AssignmentGroupCode: "",
        };
      }

      if (name === "Semester") {
        return {
          ...next,
          Section: "",
          SubjectNameCode: "",
          AssignmentGroupCode: "",
        };
      }

      if (name === "Section") {
        return { ...next, AssignmentGroupCode: "" };
      }

      return next;
    });

    setSelectedProject(null);
    setSelectedStudents([]);
  };

  const handleAssignStudents = async () => {
    if (!selectedProject?.ProjectID) {
      setError("Select a project");
      return;
    }

    if (!selectedStudents.length) {
      setError("Select at least one student");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await axios.post(
        `${API_URL}/api/academic-projects/${selectedProject.ProjectID}/assign-students`,
        {
          SubCampusCode: scope.SubCampusCode,
          AcademicYear: scope.AcademicYear,
          Section: scope.Section,
          AssignmentGroupCode: scope.AssignmentGroupCode,
          StudentKeys: selectedStudents.map((x) => x.StudentKey),
        },
        { headers: authHeaders }
      );

      setSuccess("Students assigned successfully");
      setSelectedStudents([]);
      await refreshAll();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to assign students");
    } finally {
      setSaving(false);
    }
  };

  const handleAssignmentChange = (AssignmentID, field, value) => {
    setAssignments((prev) =>
      prev.map((row) =>
        row.AssignmentID === AssignmentID ? { ...row, [field]: value } : row
      )
    );
  };

  const handleEvaluate = async (row) => {
    setSaving(true);
    setError("");

    try {
      await axios.put(
        `${API_URL}/api/academic-projects/student-assignment/${row.AssignmentID}/evaluate`,
        {
          Marks: row.Marks,
          TeacherRemarks: row.TeacherRemarks,
          StudentStatus: "EVALUATED",
        },
        { headers: authHeaders }
      );

      setSuccess("Project graded successfully");
      await refreshAll();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to grade project");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    const value = String(status || "").toUpperCase();
    if (value === "EVALUATED" || value === "COMPLETED") return "success";
    if (value === "SUBMITTED") return "info";
    if (value === "IN_PROGRESS" || value === "ASSIGNED") return "warning";
    if (value === "LATE" || value === "OVERDUE" || value === "REJECTED") return "error";
    return "default";
  };

  const projectAssignmentsMap = useMemo(() => {
    const map = new Map();
    for (const row of assignments || []) {
      const key = String(row.ProjectID || "");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }
    return map;
  }, [assignments]);

  const filteredAssignProjects = useMemo(() => {
    const search = assignStudentSearch.trim().toLowerCase();
    return (projects || []).filter((project) => {
      if (assignProjectFilter && String(project.ProjectID) !== String(assignProjectFilter)) return false;
      if (!search) return true;
      const assigned = projectAssignmentsMap.get(String(project.ProjectID)) || [];
      return assigned.some((student) =>
        `${student.StudentID || ""} ${student.FullName || ""}`.toLowerCase().includes(search)
      );
    });
  }, [projects, assignProjectFilter, assignStudentSearch, projectAssignmentsMap]);

  const assignPageCount = Math.max(1, Math.ceil(filteredAssignProjects.length / ROWS_PER_PAGE));
  const pagedAssignProjects = filteredAssignProjects.slice(
    (assignPage - 1) * ROWS_PER_PAGE,
    assignPage * ROWS_PER_PAGE
  );

  const gradeProjectOptions = useMemo(() => {
    const map = new Map();
    for (const row of assignments || []) {
      if (!row.ProjectID) continue;
      if (!map.has(String(row.ProjectID))) {
        map.set(String(row.ProjectID), {
          ProjectID: row.ProjectID,
          ProjectNumber: row.ProjectNumber,
          ProjectTitle: row.ProjectTitle,
        });
      }
    }
    return Array.from(map.values());
  }, [assignments]);

  const gradeStatusOptions = useMemo(() =>
    [...new Set((assignments || []).map((x) => x.StudentStatus).filter(Boolean))].sort(),
  [assignments]);

  const filteredAssignments = useMemo(() => {
    const search = gradeStudentSearch.trim().toLowerCase();
    return (assignments || []).filter((row) => {
      if (gradeProjectFilter && String(row.ProjectID) !== String(gradeProjectFilter)) return false;
      if (gradeStatusFilter && String(row.StudentStatus || "") !== gradeStatusFilter) return false;
      if (search && !`${row.StudentID || ""} ${row.FullName || ""}`.toLowerCase().includes(search)) return false;
      return true;
    });
  }, [assignments, gradeProjectFilter, gradeStatusFilter, gradeStudentSearch]);

  const gradePageCount = Math.max(1, Math.ceil(filteredAssignments.length / ROWS_PER_PAGE));
  const pagedAssignments = filteredAssignments.slice(
    (gradePage - 1) * ROWS_PER_PAGE,
    gradePage * ROWS_PER_PAGE
  );

  useEffect(() => {
    setAssignPage(1);
    setExpandedProjectID(null);
  }, [assignProjectFilter, assignStudentSearch, scope.AssignmentGroupCode, scope.Section]);

  useEffect(() => {
    setGradePage(1);
    setExpandedAssignmentID(null);
  }, [gradeProjectFilter, gradeStatusFilter, gradeStudentSearch, scope.AssignmentGroupCode, scope.Section]);

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
              Academic Projects - Class Teacher
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Assign HOD-published projects, grade student work, and monitor progress
            </Typography>
          </Box>

          <Button
            variant="outlined"
            sx={{
              ...outlinedBtnSx,
              color: "white",
              borderColor: "rgba(255,255,255,0.55)",
            }}
            startIcon={<RefreshIcon />}
            onClick={refreshAll}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={2500} onClose={() => setSuccess("")}>
              <Alert severity="success">{success}</Alert>
            </Snackbar>
          )}

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
              Select Class Scope
            </Typography>

            <Grid container spacing={1.5}>
              {[
                ["AcademicYear", "Academic Year"],
                ["SubCampusCode", "SubCampus"],
                ["DegreeCode", "Degree"],
                ["BranchCode", "Branch"],
                ["Regulation", "Regulation"],
                ["Semester", "Semester"],
                ["Section", "Section"],
                ["AssignmentGroupCode", "Assignment Group"],
              ].map(([name, label]) => (
                <Grid item xs={12} sm={8} md={1.5} key={name}>
                  <TextField
                    select
                    label={label}
                    name={name}
                    value={scope[name] || ""}
                    onChange={handleScopeChange}
                    fullWidth
                    size="small"
                    sx={inputSx}
                    disabled={name === "AssignmentGroupCode" && !scope.Section}
                  >
                    <MenuItem value="">
                      {name === "AssignmentGroupCode" ? "Select Assignment Group" : "Select"}
                    </MenuItem>
                    {(scopeOptions[name] || []).map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}
            {/*
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={classScopeOptions || []}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    (classScopeOptions || []).find(
                      (x) =>
                        x.Regulation === scope.Regulation &&
                        x.SubjectNameCode === scope.SubjectNameCode &&
                        x.Section === scope.Section
                    ) || null
                  }
                  onChange={(_, value) => {
                    setScope((prev) => ({
                      ...prev,
                      Regulation: value?.Regulation || "",
                      SubjectNameCode: value?.SubjectNameCode || "",
                      Section: value?.Section || "",
                    }));
                
                    setSelectedProject(null);
                    setSelectedStudents([]);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Regulation / SubjectNameCode / Section"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>
              */}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={surfaceSx}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                px: 1,
                borderBottom: `1px solid ${BORDER}`,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
                "& .Mui-selected": { color: THEME_ORANGE },
                "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE },
              }}
            >
              <Tab label="Assign Projects" icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Grade Projects" icon={<GradingIcon />} iconPosition="start" />
              <Tab label="Monitor / Analytics" icon={<AnalyticsIcon />} iconPosition="start" />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ p: 2 }}>
                {!scope.AcademicYear || !scope.Section || !scope.AssignmentGroupCode ? (
                  <Typography sx={{ color: "text.secondary" }}>
                    Select academic year, section, and assignment group to view published projects.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5, mb: 1.5 }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Project Filter"
                              value={assignProjectFilter}
                              onChange={(e) => setAssignProjectFilter(e.target.value)}
                              sx={inputSx}
                            >
                              <MenuItem value="">All Projects</MenuItem>
                              {(projects || []).map((project) => (
                                <MenuItem key={project.ProjectID} value={project.ProjectID}>
                                  #{project.ProjectNumber || "-"} - {project.ProjectTitle}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Search Student ID / Name"
                              value={assignStudentSearch}
                              onChange={(e) => setAssignStudentSearch(e.target.value)}
                              placeholder="Search assigned students"
                              sx={inputSx}
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      <Paper elevation={0} sx={tableWrapSx}>
                        <TableContainer component="div">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={theadRowSx}>
                                <TableCell />
                                <TableCell>Group</TableCell>
                                <TableCell>Project</TableCell>
                                <TableCell>Domain</TableCell>
                                <TableCell>Limit</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Action</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {pagedAssignProjects.map((row, idx) => {
                                const isExpanded = expandedProjectID === row.ProjectID;
                                const assignedStudents = projectAssignmentsMap.get(String(row.ProjectID)) || [];
                                return (
                                  <React.Fragment key={row.ProjectID}>
                                    <TableRow
                                      sx={{ ...zebraRowSx(idx), cursor: "pointer" }}
                                      onClick={() =>
                                        setExpandedProjectID((prev) =>
                                          prev === row.ProjectID ? null : row.ProjectID
                                        )
                                      }
                                    >
                                      <TableCell>
                                        {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                      </TableCell>
                                      <TableCell>
                                        <Chip size="small" label={row.AssignmentGroupCode || "-"} />
                                        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mt: 0.5 }}>
                                          Project #{row.ProjectNumber || "-"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography sx={{ fontWeight: 900 }}>{row.ProjectTitle}</Typography>
                                        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                                          {row.ProblemStatement || "-"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>{row.Domain || "-"}</TableCell>
                                      <TableCell>{row.AssignedCount || 0}/{row.MaxStudentsAllowed || 0}</TableCell>
                                      <TableCell>
                                        {row.IsLimitReached ? (
                                          <Chip color="error" size="small" label="Limit Reached" />
                                        ) : (
                                          <Chip color="success" size="small" label="Available" />
                                        )}
                                      </TableCell>
                                      <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          sx={primaryBtnSx}
                                          disabled={!!row.IsLimitReached}
                                          onClick={() => setSelectedProject(row)}
                                        >
                                          Select
                                        </Button>
                                      </TableCell>
                                    </TableRow>

                                    <TableRow>
                                      <TableCell colSpan={7} sx={{ p: 0, borderBottom: isExpanded ? undefined : 0 }}>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ p: 2, backgroundColor: "rgba(25,118,210,0.03)" }}>
                                            <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                                              Students Assigned to this Project ({assignedStudents.length})
                                            </Typography>
                                            <TableContainer component={Paper} elevation={0} sx={surfaceSx}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell sx={{ fontWeight: 900 }}>Student ID</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Student Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Due Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 900 }}>Assigned On</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {assignedStudents.map((student) => (
                                                    <TableRow key={student.AssignmentID}>
                                                      <TableCell>{student.StudentID || "-"}</TableCell>
                                                      <TableCell>{student.FullName || "-"}</TableCell>
                                                      <TableCell>{formatDate(student.DueDate || row.EndDate)}</TableCell>
                                                      <TableCell>
                                                        <Chip
                                                          size="small"
                                                          color={getStatusColor(student.StudentStatus)}
                                                          label={student.StudentStatus || "ASSIGNED"}
                                                        />
                                                      </TableCell>
                                                      <TableCell>{formatDate(student.AssignedDateTime)}</TableCell>
                                                    </TableRow>
                                                  ))}
                                                  {!assignedStudents.length && (
                                                    <TableRow>
                                                      <TableCell colSpan={5}>No students assigned to this project yet.</TableCell>
                                                    </TableRow>
                                                  )}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              })}

                              {!filteredAssignProjects.length && (
                                <TableRow>
                                  <TableCell colSpan={7}>No projects match the selected filters.</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {filteredAssignProjects.length > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                            <Pagination
                              count={assignPageCount}
                              page={Math.min(assignPage, assignPageCount)}
                              onChange={(_, page) => setAssignPage(page)}
                              color="primary"
                              size="small"
                            />
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                        <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>Assign Students</Typography>
                        <Typography sx={{ mb: 0.5, color: "text.secondary" }}>
                          Group: {scope.AssignmentGroupCode || "Not selected"}
                        </Typography>
                        <Typography sx={{ mb: 1, color: "text.secondary" }}>
                          Project: {selectedProject?.ProjectTitle || "Not selected"}
                        </Typography>

                        <Autocomplete
                          multiple
                          options={students}
                          getOptionLabel={(option) => `${option.StudentID || ""} - ${option.FullName || ""}`}
                          value={selectedStudents}
                          noOptionsText={
                            scope.AssignmentGroupCode
                              ? `No unassigned students available for ${scope.AssignmentGroupCode}`
                              : "Select an assignment group first"
                          }
                          onChange={(_, value) => setSelectedStudents(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Students"
                              helperText={
                                selectedProject
                                  ? `Only students not yet assigned in ${scope.AssignmentGroupCode} are shown.`
                                  : "Select a project first."
                              }
                              size="small"
                              sx={inputSx}
                            />
                          )}
                        />

                        <Button
                          variant="contained"
                          sx={{ ...primaryBtnSx, mt: 2 }}
                          onClick={handleAssignStudents}
                          disabled={saving || !selectedProject}
                        >
                          Assign Selected Students
                        </Button>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ p: 2 }}>
                <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5, mb: 1.5 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Project Filter"
                        value={gradeProjectFilter}
                        onChange={(e) => setGradeProjectFilter(e.target.value)}
                        sx={inputSx}
                      >
                        <MenuItem value="">All Projects</MenuItem>
                        {gradeProjectOptions.map((project) => (
                          <MenuItem key={project.ProjectID} value={project.ProjectID}>
                            #{project.ProjectNumber || "-"} - {project.ProjectTitle}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search Student ID / Name"
                        value={gradeStudentSearch}
                        onChange={(e) => setGradeStudentSearch(e.target.value)}
                        sx={inputSx}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Status Filter"
                        value={gradeStatusFilter}
                        onChange={(e) => setGradeStatusFilter(e.target.value)}
                        sx={inputSx}
                      >
                        <MenuItem value="">All Statuses</MenuItem>
                        {gradeStatusOptions.map((status) => (
                          <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Paper>

                <Paper elevation={0} sx={tableWrapSx}>
                  <TableContainer component="div">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={theadRowSx}>
                          <TableCell />
                          <TableCell>Group</TableCell>
                          <TableCell>Project</TableCell>
                          <TableCell>Student</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Marks</TableCell>
                          <TableCell>Remarks</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {pagedAssignments.map((row, idx) => {
                          let checklist = row.ReadmeChecklistJson || {};
                          if (typeof checklist === "string") {
                            try { checklist = JSON.parse(checklist); } catch { checklist = {}; }
                          }
                          const isExpanded = expandedAssignmentID === row.AssignmentID;
                          return (
                            <React.Fragment key={row.AssignmentID}>
                              <TableRow
                                sx={{ ...zebraRowSx(idx), cursor: "pointer" }}
                                onClick={() =>
                                  setExpandedAssignmentID((prev) =>
                                    prev === row.AssignmentID ? null : row.AssignmentID
                                  )
                                }
                              >
                                <TableCell>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
                                <TableCell>{row.AssignmentGroupCode || "-"}</TableCell>
                                <TableCell>{row.ProjectTitle}</TableCell>
                                <TableCell>{row.StudentID || ""} - {row.FullName || ""}</TableCell>
                                <TableCell>{formatDate(row.DueDate)}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    color={getStatusColor(row.StudentStatus)}
                                    label={row.StudentStatus || "ASSIGNED"}
                                    sx={{ fontWeight: 800 }}
                                  />
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={row.Marks || ""}
                                    onChange={(e) => handleAssignmentChange(row.AssignmentID, "Marks", e.target.value)}
                                    sx={inputSx}
                                  />
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <TextField
                                    size="small"
                                    value={row.TeacherRemarks || ""}
                                    onChange={(e) => handleAssignmentChange(row.AssignmentID, "TeacherRemarks", e.target.value)}
                                    sx={inputSx}
                                  />
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    sx={primaryBtnSx}
                                    startIcon={<SaveIcon />}
                                    onClick={() => handleEvaluate(row)}
                                    disabled={saving}
                                  >
                                    Grade
                                  </Button>
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell colSpan={9} sx={{ p: 0, borderBottom: isExpanded ? undefined : 0 }}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ p: 2, backgroundColor: "rgba(25,118,210,0.03)" }}>
                                      <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>Submitted Project Details</Typography>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                          <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                                            <Stack spacing={1}>
                                              <Typography><b>GitHub:</b>{" "}{row.GitHubUrl ? <Link href={normalizeExternalUrl(row.GitHubUrl)} target="_blank" rel="noopener noreferrer">{row.GitHubUrl}</Link> : "-"}</Typography>
                                              <Typography><b>YouTube:</b>{" "}{row.YouTubeUrl ? <Link href={normalizeExternalUrl(row.YouTubeUrl)} target="_blank" rel="noopener noreferrer">{row.YouTubeUrl}</Link> : "-"}</Typography>
                                              <Typography><b>Notion Prompt Log:</b>{" "}{row.NotionPromptUrl ? <Link href={normalizeExternalUrl(row.NotionPromptUrl)} target="_blank" rel="noopener noreferrer">{row.NotionPromptUrl}</Link> : "-"}</Typography>
                                              <Typography><b>Deployment:</b>{" "}{row.DeploymentUrl ? <Link href={normalizeExternalUrl(row.DeploymentUrl)} target="_blank" rel="noopener noreferrer">{row.DeploymentUrl}</Link> : "-"}</Typography>
                                              <Typography><b>Due Date:</b> {formatDate(row.DueDate)}</Typography>
                                              <Typography><b>Submitted:</b> {row.SubmittedDateTime ? formatDateTimeIST(row.SubmittedDateTime) : "-"}</Typography>
                                            </Stack>
                                          </Paper>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                                            <Typography sx={{ fontWeight: 900, mb: 1 }}>README Checklist</Typography>
                                            <Stack spacing={0.75}>
                                              {Object.keys(checklist).length ? (
                                                Object.entries(checklist).map(([key, value]) => (
                                                  <Typography key={key} sx={{ fontSize: "0.9rem" }}>
                                                    <b>{formatChecklistLabel(key)}:</b> {typeof value === "boolean" ? (value ? "Yes" : "No") : String(value ?? "-")}
                                                  </Typography>
                                                ))
                                              ) : (
                                                <Typography color="text.secondary">No checklist submitted.</Typography>
                                              )}
                                            </Stack>
                                          </Paper>
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}

                        {!filteredAssignments.length && (
                          <TableRow>
                            <TableCell colSpan={9}>No assignments match the selected filters.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {filteredAssignments.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                      <Pagination
                        count={gradePageCount}
                        page={Math.min(gradePage, gradePageCount)}
                        onChange={(_, page) => setGradePage(page)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

            {tab === 2 && (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {[
                    ["Total Projects", analytics.TotalProjects || 0],
                    ["Assigned", analytics.TotalAssigned || 0],
                    ["Submitted", analytics.Submitted || 0],
                    ["Evaluated", analytics.Evaluated || 0],
                    ["Average Marks", Number(analytics.AverageMarks || 0).toFixed(2)],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={2.4} key={label}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...surfaceSx,
                          p: 2,
                          textAlign: "center",
                          backgroundColor: "rgba(25,118,210,0.03)",
                        }}
                      >
                        <Typography sx={{ color: "text.secondary", fontWeight: 800 }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontWeight: 900, fontSize: "1.8rem", color: BLUE }}>
                          {value}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default AcademicProjectClassTeacherManager;